/** @jsxImportSource @emotion/react */
import tw from "twin.macro";

import LogoPNG from "../logo.png";
import { css } from "@emotion/css";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Popover,
  Select,
} from "@material-ui/core";
import {
  addDays,
  startOfMonth,
  startOfToday,
  startOfTomorrow,
  startOfWeek,
} from "date-fns";
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { FiCalendar, FiHelpCircle } from "react-icons/fi";
import { HiOutlineCog, HiOutlineLightBulb } from "react-icons/hi";
import { Auth } from "../Auth";
import {
  isCompleted,
  isInBacklog,
  isInDayBacklog,
  isInMonthBacklog,
  isInWeekBacklog,
  isOverdue,
  putInBacklog,
  putInDayBacklog,
  putInMonthBacklog,
  putInWeekBacklog,
} from "../state/data/utils";
import {
  useViewOptions,
  viewOptionsStore,
} from "../state/state/viewOptionsStore";
import { NoteList } from "./NoteList";
import { ReadOnlyNoteList } from "./ReadOnlyNoteList";
import { useCalendarEvents } from "../state/state/useCalendarEvents";

export const Sidebar = () => {
  const viewControlsState = usePopupState({
    variant: "popover",
    popupId: "view-controls",
  });

  const viewOptions = useViewOptions();

  return (
    <div
      className={css`
        height: 100%;
        overflow-x: hidden;
      `}
      tw="flex flex-col"
    >
      <hr />
      <div
        className={css`
          height: 64px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.202);
          display: flex;
          place-items: center;
          margin-left: 10px;
          margin-right: 10px;
        `}
      >
        {/* <div tw="mr-auto">
          <TextField
            id="search"
            label="Search"
            onChange={(e) => {
              console.log("feeling good");
            }}
          />
        </div> */}
        <div tw="mr-auto">
          <a href="https://www.todoyogi.com/" target="_blank" rel="noreferrer">
            <img
              src={LogoPNG}
              alt="Todoyogi Logo"
              className={css`
                height: 40px;
              `}
            />
          </a>
        </div>
        <div
          className={css`
            margin-right: 8px;
          `}
        >
          <Auth />
        </div>
        <div
          // tw="ml-auto"
          className={css`
            display: flex;
          `}
        >
          <a
            href="https://www.todoyogi.com/#kb"
            target="_blank"
            rel="noreferrer"
            className={css`
              color: black;
            `}
            title="Knowledge base"
          >
            <FiHelpCircle
              className={css`
                font-size: 2.1em;
                margin: 0.25em;
                cursor: pointer;
              `}
            />
          </a>
          <a
            href="https://www.todoyogi.com/#tuto"
            target="_blank"
            rel="noreferrer"
            className={css`
              color: black;
            `}
            title="Tutorials"
          >
            <HiOutlineLightBulb
              className={css`
                font-size: 2.1em;
                margin: 0.25em;
                cursor: pointer;
              `}
            />
          </a>
          <HiOutlineCog
            className={css`
              font-size: 2.1em;
              margin: 0.25em;
              cursor: pointer;
            `}
            title="Layout options"
            {...bindTrigger(viewControlsState)}
          />
          <Popover
            {...bindPopover(viewControlsState)}
            anchorOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
          >
            <div tw="m-4 mx-2">
              <div tw="m-2">
                <FormControl style={{ width: "160px" }}>
                  <InputLabel id="colwidth-label">Column Width</InputLabel>
                  <Select
                    labelId="colwidth-label"
                    id="colwidth"
                    value={viewOptions?.colWidth}
                    onChange={(e) => {
                      //@ts-ignore
                      viewOptionsStore.update((state) => ({
                        ...state,
                        colWidth: e.target.value,
                      }));
                    }}
                  >
                    <MenuItem value={"COMPACT"}>Compact</MenuItem>
                    <MenuItem value={"MEDIUM"}>Medium</MenuItem>
                    <MenuItem value={"WIDE"}>Wide</MenuItem>
                  </Select>
                  {/* <FormHelperText>Some important helper text</FormHelperText> */}
                </FormControl>
              </div>
              <div tw="m-6" />
              <div tw="m-2">
                <FormControl style={{ width: "160px" }}>
                  <InputLabel id="colheight-label">Column Height</InputLabel>
                  <Select
                    labelId="colheight-label"
                    id="colheight"
                    value={viewOptions?.colHeight}
                    onChange={(e) => {
                      //@ts-ignore
                      viewOptionsStore.update((state) => ({
                        ...state,
                        colHeight: e.target.value,
                      }));
                    }}
                  >
                    <MenuItem value={"COMPACT"}>Compact</MenuItem>
                    <MenuItem value={"MEDIUM"}>Medium</MenuItem>
                    <MenuItem value={"TALL"}>Tall</MenuItem>
                  </Select>
                  <FormHelperText>
                    Controls how much of a day is visible on screen
                  </FormHelperText>
                </FormControl>
              </div>
            </div>
          </Popover>
        </div>
      </div>
      <div tw="overflow-y-auto overflow-x-hidden">
        <div tw="py-2">
          <h2 tw="text-2xl px-2 py-1">Overdue</h2>
          <ReadOnlyNoteList selector={isOverdue} />
        </div>
        <div>
          <NoteList
            selector={isInDayBacklog(startOfToday())}
            effector={putInDayBacklog(startOfToday())}
            id={"today-area"}
          >
            <h2 tw="text-2xl px-2 py-1">Today</h2>
          </NoteList>
        </div>
        <div>
          <NoteList
            selector={isInDayBacklog(startOfTomorrow())}
            effector={putInDayBacklog(startOfTomorrow())}
            id={"tomorrow-area"}
          >
            <h2 tw="text-2xl px-2 py-1">Tomorrow</h2>
          </NoteList>
        </div>
        <div>
          <NoteList
            selector={isInWeekBacklog(
              startOfWeek(new Date(), { weekStartsOn: 1 })
            )}
            effector={putInWeekBacklog(
              startOfWeek(new Date(), { weekStartsOn: 1 })
            )}
            id={"today-week-area"}
          >
            <h2 tw="text-2xl px-2 py-1">This week</h2>
          </NoteList>
        </div>
        <div>
          <NoteList
            selector={isInWeekBacklog(
              startOfWeek(addDays(new Date(), 7), { weekStartsOn: 1 })
            )}
            effector={putInWeekBacklog(
              startOfWeek(addDays(new Date(), 7), { weekStartsOn: 1 })
            )}
            id={"next-week-area"}
          >
            <h2 tw="text-2xl px-2 py-1">Next week</h2>
          </NoteList>
        </div>
        <div>
          <NoteList
            selector={isInMonthBacklog(startOfMonth(new Date()))}
            effector={putInMonthBacklog(startOfMonth(new Date()))}
            id={"today-month-area"}
          >
            <h2 tw="text-2xl px-2 py-1 pt-2">This month</h2>
          </NoteList>
        </div>
        <div>
          <NoteList
            selector={isInMonthBacklog(startOfMonth(addDays(new Date(), 30)))}
            effector={putInMonthBacklog(startOfMonth(addDays(new Date(), 30)))}
            id={"next-month-area"}
          >
            <h2 tw="text-2xl px-2 py-1 pt-2">Next month</h2>
          </NoteList>
        </div>
        <div>
          <NoteList
            selector={isInBacklog}
            effector={putInBacklog}
            id={"backlog-area"}
          >
            <h2 tw="text-2xl px-2 py-1">Unscheduled</h2>
          </NoteList>
        </div>
        <div tw="py-2">
          <h2 tw="text-2xl px-2 py-1">Completed</h2>
          <ReadOnlyNoteList selector={isCompleted} />
        </div>
      </div>
    </div>
  );
};
